/**
 * NEW: Adobe Analytics Integration Component
 * Created: February 2026
 * 
 * This component integrates Adobe Analytics (Tealium) tracking across W Insurance digital portals.
 * It handles external script loading with retry logic and subscribes to analytics events.
 * 
 * Features:
 * - Automatic _satellite script detection with retries
 * - Subscribes to route changes, quote loads, and purchase completions
 * - Comprehensive logging via WMICRemoteLogger
 * - Optional redirect functionality
 */
/* eslint-disable no-use-before-define */
import { useState, useEffect } from 'react';
import { subscribe, JUTRO_TOPICS } from '@jutro/events';
import appConfig from 'app-config';
import { WMICRemoteLogger, WMICTopics } from 'wmic-portals-utils-js';

// Mirrors WMICRegionUrlUtil's storage key (avoids adding wmic-portals-url-js as a package dependency here)
const getRegion = () => localStorage.getItem('region');

/**
 * NEW: Custom hook to check if Tealium (utag) is loaded
 * Waits for the script to load with retries (max 10 retries, 500ms delay each)
 */
function useAdobeAnalyticsScript(maxRetries = 10, retryDelay = 500) {
    const [isLoaded, setIsLoaded] = useState(false);
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        // Check if utag is loaded
        if (typeof window.utag !== 'undefined' && typeof window.utag.link === 'function') {
            setIsLoaded(true);
            return;
        }

        // If not loaded and we haven't exceeded retries, try again
        if (retryCount < maxRetries) {
            const timer = setTimeout(() => {
                if (typeof window.utag !== 'undefined' && typeof window.utag.link === 'function') {
                    setIsLoaded(true);
                } else {
                    setRetryCount(prev => prev + 1);
                }
            }, retryDelay);

            return () => clearTimeout(timer);
        } else {
            console.error('[AdobeAnalytics] Failed to load Tealium utag after max retries');
        }
    }, [retryCount, maxRetries, retryDelay]);

    return isLoaded;
}

function AdobeAnalytics({ 
    applicationName = 'W Insurance Digital Portal', 
    productType = 'Insurance Quote',
    enableRedirect = false,
    redirectUrl = null 
}) {
    const [isInitialized, setIsInitialized] = useState(false);
    const [logger, setLogger] = useState(null);
    const isAdobeScriptLoaded = useAdobeAnalyticsScript();

    
    useEffect(() => {
        const wmicLogger = WMICRemoteLogger.getWMICRemoteLogger(
            'AdobeAnalytics',
            'Analytics',
            { pathname: window.location.pathname },
            {},
            null
        );
        setLogger(wmicLogger);
    }, []);

    
    useEffect(() => {
        if (isAdobeScriptLoaded && window.utag && !isInitialized && logger) {
            try {
                
                
                subscribe(WMICTopics.ROUTE_CHANGED, (evtData) => {
                    try {
                        console.log('[AdobeAnalytics] Route changed:', evtData);

                        // Per-page tagging guide variable map: only the base UDO variables are
                        // sent for every page view; extra fields (ins_quoteid, premium, cta, error,
                        // IsAuthenticated, etc.) are opt-in and only included when the publishing
                        // page explicitly provides them, since each page's guide differs.
                        const dataLayerUpdate = {
                            page: evtData.page || evtData.page_name || '',
                            channel: evtData.channel || 'insurance',
                            subchannel1: evtData.subchannel1 || 'renters',
                            subchannel2: evtData.subchannel2 || evtData.page_sub_section_1 || '',
                            sessionid: sessionStorage.getItem('X_GWRE_SESSION') || '',
                            // Falls back to the jurisdiction region, then defaults to CA if state is still unknown
                            state: (evtData.state || evtData.policy_state || getRegion() || (window.wins_data_layer && window.wins_data_layer.state) || 'CA').toLowerCase(),
                            flow: evtData.flow || (window.wins_data_layer && window.wins_data_layer.flow) || ''
                        };

                        if (evtData.quoteId || evtData.quote_id || evtData.ins_quoteid || (window.wins_data_layer && window.wins_data_layer.ins_quoteid)) {
                            dataLayerUpdate.ins_quoteid = evtData.quoteId || evtData.quote_id || evtData.ins_quoteid || (window.wins_data_layer && window.wins_data_layer.ins_quoteid);
                        }

                        if (evtData.ins_quote_id || evtData.quoteId || (window.wins_data_layer && window.wins_data_layer.ins_quote_id)) {
                            dataLayerUpdate.ins_quote_id = evtData.ins_quote_id || (evtData.quoteId ? 'IQQ' + evtData.quoteId : '') || (window.wins_data_layer && window.wins_data_layer.ins_quote_id);
                        }

                        if (evtData.premium) {
                            dataLayerUpdate.premium = evtData.premium;
                        }

                        if (evtData.category) {
                            dataLayerUpdate.category = evtData.category;
                        }

                        if (evtData.product) {
                            dataLayerUpdate.product = evtData.product;
                        }

                        if (evtData.quantity) {
                            dataLayerUpdate.quantity = evtData.quantity;
                        }

                        if (evtData.logged_in_status || evtData.IsAuthenticated) {
                            dataLayerUpdate.IsAuthenticated = evtData.logged_in_status || evtData.IsAuthenticated;
                        }

                        if (evtData.cta) {
                            dataLayerUpdate.cta = evtData.cta;
                        }

                        if (evtData.error) {
                            dataLayerUpdate.error = evtData.error;
                        }

                        if (window.updateDataLayer && typeof window.updateDataLayer === 'function') {
                            window.updateDataLayer(dataLayerUpdate);
                        }

                        if (window.utag && typeof window.utag.view === 'function') {
                            window.utag.view(dataLayerUpdate);
                        } else {
                            console.warn('[AdobeAnalytics] utag not loaded, skipping route tracking');
                        }
                    } catch (err) {
                        console.error('[AdobeAnalytics] Error in ROUTE_CHANGED handler:', err);
                    }
                });


                subscribe(WMICTopics.PURCHASE_COMPLETED, (evtData) => {
                    try {
                       
                       
                    const quoteId = evtData.quoteId || evtData.quote_id;
                    const premium = evtData.premium || evtData.order_total;
                    const state = evtData.state;
                    const coverageType = evtData.coverageType || evtData.coverage_type;
                    const productType = evtData.productType || evtData.product_type;
                    const policyNumber = evtData.policyNumber;
                    
                        
                        if (window.updateDataLayer && typeof window.updateDataLayer === 'function') {
                        const purchaseDataUpdate = {
                            channel: evtData.channel || 'insurance',                                
                            subchannel1: evtData.subchannel1 || 'renters',                          
                            subchannel2: evtData.subchannel2 || 'bind',                            
                            category: evtData.category || ['insurance quote'],                       
                            product: evtData.product || ['renters'],                                 
                            quantity: evtData.quantity || ['1'],                                    
                            pageurl: window.location.href,                                          
                            platform: /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop',  
                            sessionid: sessionStorage.getItem('X_GWRE_SESSION') || '',              
                            ins_quoteid: quoteId || '',                                             
                            ins_quote_id: quoteId ? 'IQQ' + quoteId : '',                          
                            state: (state || '').toLowerCase(),                                      
                            premium: premium || '',
                            coverage_type: coverageType || 'renters',
                            product_type: productType || 'Homeowners'
                        };
                        
                        
                        if (evtData.flow) {
                                purchaseDataUpdate.flow = evtData.flow;  
                            }
                            
                            window.updateDataLayer(purchaseDataUpdate);
                        }
                        
                        if (window.utag && typeof window.utag.link === 'function') {
                        window.utag.link({
                            ...(window.wins_data_layer || {}),
                            event_name: 'purchase_complete',
                            tealium_event: 'purchase',
                            ...evtData,
                            state: (evtData.state || (window.wins_data_layer && window.wins_data_layer.state) || '').toLowerCase()
                            });
                        } else {
                            console.warn('[AdobeAnalytics] utag not loaded, skipping purchase tracking');
                        }
                    } catch (err) {
                        console.error('[AdobeAnalytics] Error in PURCHASE_COMPLETED handler:', err);
                    }
                });

                
                
                subscribe(WMICTopics.CTA_CLICK, (evtData) => {
                    try {
                        const dl = window.wins_data_layer || {};

                        if (window.updateDataLayer && typeof window.updateDataLayer === 'function' && evtData.cta) {
                            window.updateDataLayer({ cta: evtData.cta });
                        }

                        // Per-page tagging guide variable map for CTA click tags
                        const ctaPayload = {
                            channel: evtData.channel || dl.channel || 'insurance',
                            subchannel1: evtData.subchannel1 || dl.subchannel1 || 'renters',
                            subchannel2: evtData.subchannel2 || dl.subchannel2 || '',
                            page: evtData.page || dl.page || '',
                            sessionid: sessionStorage.getItem('X_GWRE_SESSION') || '',
                            state: (evtData.state || dl.state || '').toLowerCase(),
                            flow: evtData.flow || dl.flow || '',
                            cta: evtData.cta || dl.cta || ''
                        };

                        if (evtData.ins_quoteid || dl.ins_quoteid) {
                            ctaPayload.ins_quoteid = evtData.ins_quoteid || dl.ins_quoteid;
                        }

                        if (evtData.ins_quote_id || dl.ins_quote_id) {
                            ctaPayload.ins_quote_id = evtData.ins_quote_id || dl.ins_quote_id;
                        }

                        if (window.utag && typeof window.utag.link === 'function') {
                            window.utag.link(ctaPayload);
                        } else {
                            console.warn('[AdobeAnalytics] utag not loaded, skipping CTA tracking');
                        }
                    } catch (err) {
                        console.error('[AdobeAnalytics] Error in CTA_CLICK handler:', err);
                    }
                });

                
                subscribe(WMICTopics.FORM_ERROR, (evtData) => {
                    try {
                        // NEW: Log error tracking for verification
                        console.log('[AdobeAnalytics] Error tracked:', {
                            error: evtData.error,
                            error_message: evtData.error_message,
                            page_name: evtData.page_name,
                            quoteId: evtData.quoteId,
                            completeDataLayer: window.wins_data_layer,
                            eventData: evtData
                        });
                       
                        if (window.updateDataLayer && typeof window.updateDataLayer === 'function' && evtData.error) {
                            window.updateDataLayer({
                                error: evtData.error 
                            });
                        }
                        
                        if (window.utag && typeof window.utag.link === 'function') {
                            window.utag.link({
                                ...(window.wins_data_layer || {}),
                                event_name: 'form_error',
                                tealium_event: 'error',
                                ...evtData,
                                state: (evtData.state || (window.wins_data_layer && window.wins_data_layer.state) || '').toLowerCase()
                            });
                        } else {
                            console.warn('[AdobeAnalytics] utag not loaded, skipping error tracking');
                        }
                    } catch (err) {
                        console.error('[AdobeAnalytics] Error in FORM_ERROR handler:', err);
                    }
                });

                // Modal interaction tracking (OneINC payment modals, etc.)
                subscribe(WMICTopics.MODAL_INTERACTION, (evtData) => {
                    try {
                        console.log('[AdobeAnalytics] Modal interaction:', {
                            modal_name: evtData.modal_name,
                            modal_action: evtData.modal_action,
                            page_name: evtData.page_name,
                            quoteId: evtData.quoteId,
                            completeDataLayer: window.wins_data_layer,
                            eventData: evtData
                        });

                        if (window.updateDataLayer && typeof window.updateDataLayer === 'function' && evtData.error) {
                            window.updateDataLayer({ error: evtData.error });
                        }

                        // Fire Tealium event as utag.view for modal page views
                        if (window.utag && typeof window.utag.view === 'function') {
                            window.utag.view({
                                ...(window.wins_data_layer || {}),
                                page: evtData.page || evtData.page_name,
                                channel: evtData.channel || 'insurance',
                                subchannel1: evtData.subchannel1 || 'renters',
                                subchannel2: evtData.subchannel2 || 'quote',
                                flow: evtData.flow || (window.wins_data_layer && window.wins_data_layer.flow) || '',
                                ins_quoteid: evtData.ins_quoteid || evtData.quoteId,
                                sessionid: sessionStorage.getItem('X_GWRE_SESSION') || '',
                                ...evtData,
                                state: (evtData.state || (window.wins_data_layer && window.wins_data_layer.state) || '').toLowerCase()
                            });
                        } else {
                            console.warn('[AdobeAnalytics] utag not loaded, skipping modal tracking');
                        }
                    } catch (err) {
                        console.error('[AdobeAnalytics] Error in MODAL_INTERACTION handler:', err);
                    }
                });

                setIsInitialized(true);
                logger.info(`Adobe Analytics initialized successfully with Tealium utag for ${applicationName}`);
                
            } catch (error) {
                logger.error(`Error initializing Adobe Analytics: ${error.message}`);
                console.error('[AdobeAnalytics] Initialization error:', error);
            }
        } else if (!isAdobeScriptLoaded && !isInitialized && logger) {
            logger.error(`Session: ${sessionStorage.X_GWRE_SESSION || 'N/A'}, Failed to load Adobe Analytics - utag not available after retries`);
        }
        
        
    }, [isAdobeScriptLoaded, isInitialized, logger]);
 
    useEffect(() => {
        if (!logger) return;

        if (document?.referrer) {
            logger.info(`Website initial load, Previous Page: ${document.referrer}`);
        }

        
     
        if (enableRedirect && redirectUrl) {
            const currentPath = window.location.pathname;
            const expectedApp = applicationName.toLowerCase().replace(/\s+/g, '-');
            
            
            if (!currentPath.includes(expectedApp)) {
                logger.info(`Redirecting from ${currentPath} to ${redirectUrl}`);
                window.location.href = redirectUrl;
            }
        }
       
    }, [logger]);

    return null;
}

export default AdobeAnalytics;
